import React, { useState, useEffect } from 'react';
import { Advertisement, CreateAdvertisementPayload, UpdateAdvertisementPayload, AdvertisementType } from '../../services/advertisementService';
import styles from './AdvertisementFormModal.module.css';

export interface AdvertisementFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateAdvertisementPayload | UpdateAdvertisementPayload) => void;
  initialData?: Advertisement | null;
  isLoading?: boolean;
  categories?: Array<{ id: number; category_name: string }>;
  stores?: Array<{ id: number; name: string }>;
  products?: Array<{ id: number; product_name: string }>;
  onCategoryChange?: (categoryId: number | null) => void;
  onStoreChange?: (storeId: number | null) => void;
}

const AdvertisementFormModal: React.FC<AdvertisementFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  isLoading = false,
  categories = [],
  stores = [],
  products = [],
  onCategoryChange,
  onStoreChange,
}) => {
  const [title, setTitle] = useState('');
  const [brief, setBrief] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [webUrl, setWebUrl] = useState('');
  const [type, setType] = useState<AdvertisementType>(AdvertisementType.HOME_MAIN);
  const [isActive, setIsActive] = useState(true);
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [storeId, setStoreId] = useState<number | null>(null);
  const [productId, setProductId] = useState<number | null>(null);
  const [availableFrom, setAvailableFrom] = useState('');
  const [availableTo, setAvailableTo] = useState('');

  const toDatetimeLocal = (iso: string | null | undefined): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title || '');
      setBrief(initialData.brief || '');
      setDescription(initialData.description || '');
      setImageUrl(initialData.image_url || '');
      setImagePreview(initialData.image_url || '');
      setImageFile(null);
      setWebUrl(initialData.web_url || '');
      setType(initialData.type || AdvertisementType.HOME_MAIN);
      setIsActive(initialData.is_active !== undefined ? initialData.is_active : true);
      setCategoryId(initialData.category_id || null);
      setStoreId(initialData.store_id || null);
      setProductId(initialData.product_id || null);
      setAvailableFrom(toDatetimeLocal(initialData.available_from));
      setAvailableTo(toDatetimeLocal(initialData.available_to));
    } else {
      setTitle('');
      setBrief('');
      setDescription('');
      setImageUrl('');
      setImageFile(null);
      setImagePreview('');
      setWebUrl('');
      setType(AdvertisementType.HOME_MAIN);
      setIsActive(true);
      setCategoryId(null);
      setStoreId(null);
      setProductId(null);
      setAvailableFrom('');
      setAvailableTo('');
    }
  }, [initialData, isOpen]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    
    const submitData: any = {
      title,
      brief,
      description,
      web_url: webUrl || null,
      type,
      is_active: isActive,
      category_id: categoryId || null,
      store_id: storeId || null,
      product_id: productId || null,
      available_from: availableFrom ? new Date(availableFrom).toISOString() : null,
      available_to: availableTo ? new Date(availableTo).toISOString() : null,
    };

    if (imageFile) {
      submitData.imageFile = imageFile;
    } else if (imageUrl && !imageUrl.startsWith('data:image')) {
      submitData.image_url = imageUrl;
    }

    onSubmit(submitData);
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImageUrl('');
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const url = event.target.value;
    setImageUrl(url);
    if (url && !url.startsWith('data:image')) {
      setImagePreview(url);
      setImageFile(null);
    }
  };

  const removeImage = () => {
    setImageUrl('');
    setImageFile(null);
    setImagePreview('');
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <h3>{initialData ? 'Edit Advertisement' : 'Add New Advertisement'}</h3>
        <form onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label htmlFor="title">Title *</label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              disabled={isLoading}
              maxLength={100}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="brief">Brief *</label>
            <input
              type="text"
              id="brief"
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              required
              disabled={isLoading}
              maxLength={200}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              disabled={isLoading}
              rows={4}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="type">Type *</label>
            <select
              id="type"
              value={type}
              onChange={(e) => setType(e.target.value as AdvertisementType)}
              required
              disabled={isLoading}
            >
              <option value={AdvertisementType.HOME_MAIN}>Home Main</option>
              <option value={AdvertisementType.HOME_RECOMMENDATION}>Home Recommendation</option>
              <option value={AdvertisementType.HOME_RECENT}>Home Recent</option>
              <option value={AdvertisementType.CATEGORY_MAIN}>Category Main</option>
            </select>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="imageFile">Image</label>
            <input
              type="file"
              id="imageFile"
              accept="image/*"
              onChange={handleImageChange}
              disabled={isLoading}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="imageUrl">Or Image URL</label>
            <input
              type="url"
              id="imageUrl"
              value={imageUrl}
              onChange={handleImageUrlChange}
              disabled={isLoading}
              placeholder="https://example.com/image.jpg"
              maxLength={255}
            />
            <small className={styles.fieldHint}>
              Upload an image file or enter a direct URL to an image.
            </small>
          </div>

          {imagePreview && (
            <div className={styles.imagePreview}>
              <img src={imagePreview} alt="Advertisement preview" className={styles.previewImage} />
              <button 
                type="button" 
                onClick={removeImage} 
                className={styles.removeImageButton}
                disabled={isLoading}
              >
                Remove Image
              </button>
            </div>
          )}

          <div className={styles.formGroup}>
            <label htmlFor="webUrl">Web URL</label>
            <input
              type="url"
              id="webUrl"
              value={webUrl}
              onChange={(e) => setWebUrl(e.target.value)}
              disabled={isLoading}
              placeholder="https://example.com"
            />
          </div>

          <p className={styles.sectionTitle}>Select the add type</p>

          <div className={styles.formGroup}>
            <label htmlFor="categoryId">Category (Optional)</label>
            <select
              id="categoryId"
              value={categoryId || ''}
              onChange={(e) => {
                const next = e.target.value ? parseInt(e.target.value, 10) : null;
                setCategoryId(next);
                setStoreId(null);
                setProductId(null);
                onCategoryChange?.(next);
              }}
              disabled={isLoading}
            >
              <option value="">None</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.category_name}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="storeId">Store (Optional)</label>
            <select
              id="storeId"
              value={storeId || ''}
              onChange={(e) => {
                const next = e.target.value ? parseInt(e.target.value, 10) : null;
                setStoreId(next);
                setProductId(null);
                onStoreChange?.(next);
              }}
              disabled={isLoading}
            >
              <option value="">None</option>
              {stores.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="productId">Product (Optional)</label>
            <select
              id="productId"
              value={productId || ''}
              onChange={(e) => setProductId(e.target.value ? parseInt(e.target.value, 10) : null)}
              disabled={isLoading}
            >
              <option value="">None</option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.product_name}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.periodSection}>
            <p className={styles.periodSectionTitle}>
              Visibility period{' '}
              <span className={styles.periodSectionTitleOptional}>(optional)</span>
            </p>
            <div className={styles.periodGrid}>
              <div className={styles.formGroup}>
                <label htmlFor="availableFrom">Available from</label>
                <input
                  type="datetime-local"
                  id="availableFrom"
                  value={availableFrom}
                  onChange={(e) => setAvailableFrom(e.target.value)}
                  disabled={isLoading}
                  aria-describedby="availableFrom-hint"
                />
                <small id="availableFrom-hint" className={styles.fieldHint}>
                  Date and time when the ad becomes visible in the app.
                </small>
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="availableTo">Available to / expires</label>
                <input
                  type="datetime-local"
                  id="availableTo"
                  value={availableTo}
                  onChange={(e) => setAvailableTo(e.target.value)}
                  disabled={isLoading}
                  aria-describedby="availableTo-hint"
                />
                <small id="availableTo-hint" className={styles.fieldHint}>
                  Last moment the ad is shown. Leave empty for no end date.
                </small>
              </div>
            </div>
            <p className={styles.periodSectionFootnote}>
              If both are empty, scheduling is not applied. If only &quot;from&quot; is set, the ad shows from that time onward.
            </p>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                disabled={isLoading}
              />
              <span>Active</span>
            </label>
          </div>

          <div className={styles.formActions}>
            <button type="submit" className={styles.submitButton} disabled={isLoading}>
              {isLoading ? 'Saving...' : (initialData ? 'Save Changes' : 'Add Advertisement')}
            </button>
            <button type="button" onClick={onClose} className={styles.cancelButton} disabled={isLoading}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdvertisementFormModal;
